import { useState } from 'react';
import {
    Typography,
    FormControl,
    TextField,
    Button,
    IconButton,
    InputAdornment,
    RadioGroup,
    FormControlLabel
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/system';
import dayjs from 'dayjs';
import { KYB, Analytics } from 'lib/api';
import { KYBStepBanner } from './KYBStepBanner';
import StatusSnackbar from 'components/shared/StatusSnackbar';
import CircularComponent from 'components/shared/CircularComponent';
import CloseIcon from '@mui/icons-material/Close';
import { AttachMoney } from '@mui/icons-material';
import { KYX_ANALYTICS_EVENTS } from '../StartKYX';
import BlueToolTip from 'components/shared/BlueTooltip';
import Autocomplete from '@mui/material/Autocomplete';
import HelpIcon from '@mui/icons-material/HelpOutline';
import Radio from '@mui/material/Radio';

const useStyles = makeStyles({
    dialogBody: {
        display: 'flex',
        flexDirection: 'row',
        minHeight: '600px',
        height: '100%'
    },
    content: {
        margin: '2em',
        width: '40em'
    },
    contentTitle: {
        fontWeight: '700!important'
    },
    phoneBox: {
        marginTop: '2em'
    },
    phoneInput: {
        width: '50%'
    },
    verifyButton: {
        color: 'white!important',
        borderRadius: '25px!important',
        padding: '.5em 3em!important',
        margin: '.5em!important',
        background: '#1BABE2!important'
    },
    inputFormControl: {
        background: '#FFFFFF',
        border: '1px solid #D8DDE2',
        boxSizing: 'border-box',
        borderRadius: '3px',
        marginTop: '10px',
        width: '100%!important',
        height: '40px'
    },
    contactInforFormLabel: {
        color: '#225384',
        fontWeight: 600
    },
    businessAdressBox: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2,1fr)',
        rowGap: '1em',
        columnGap: '1em'
    },
    formControl: {
        width: '100%!important'
    },
    informationOptions: {
        marginTop: '3em',
        display: 'flex',
        justifyContent: 'space-between'
    },
    next: {
        color: 'white!important',
        borderRadius: '25px!important',
        marginLeft: '1em',
        padding: '.5em 3em!important'
    }
});

const volumeOptions = [
    { label: '$0-$0', value: '0' },
    { label: '$0.01-$5,000', value: '2500.005' },
    { label: '$5,000.01-$10,000', value: '7500.005' },
    { label: '$10,000.01-$20,000', value: '15000.005' },
    { label: '$20,000.01-$30,000', value: '25000.005' },
    { label: '$30,000.01-$60,000', value: '45000.005' },
    { label: '$60,000.01-$100,000', value: '80000.005' },
    { label: '$100,000.01-$150,000', value: '125000.005' }
];

const countOptions = [
    { label: '0-0', value: '0' },
    { label: '1-10', value: '5' },
    { label: '11-20', value: '15' },
    { label: '21-30', value: '25' },
    { label: '31-40', value: '35' },
    { label: '41-60', value: '50' },
    { label: '61-100', value: '80' },
    { label: '101-150', value: '125' }
];

export const KYBFourthStep = ({ handleClose, activePage, formValues, decreasePage, incrementPage }) => {
    const classes = useStyles();
    const [openAlert, setOpenAlert] = useState(false);
    const [label, setLabel] = useState('');
    const [projectionValues, setProjectionValues] = useState({ transaction_volume: '', transaction_count: '' });
    const [loading, setLoading] = useState(false);
    const [transactionType, setTransactionType] = useState('');

    const sendInfo = async () => {
        const body = {
            step: KYX_ANALYTICS_EVENTS.STEP_6,
            eventData: projectionValues
        };
        Analytics.createKYXEvent(body).catch(error => console.error(error));

        setLoading(true);
        try {
            await KYB.patchTransactionProjections(formValues.id, projectionValues, transactionType);
        } catch (error) {
            console.error(error);
        }
        setLoading(false);
        incrementPage();
    };

    const handleVolumeChange = (e, newVal) => {
        setProjectionValues({
            ...projectionValues,
            transaction_volume: newVal ? newVal.value : ''
        });
    };

    const handleCountChange = (e, newVal) => {
        setProjectionValues({
            ...projectionValues,
            transaction_count: newVal ? newVal.value : ''
        });
    };

    const isNextDisabled = () => {
        return !projectionValues.transaction_count || !projectionValues.transaction_volume;
    };

    const handleRadioChange = event => {
        setTransactionType(event.target.value);
    };

    return (
        <Box className={classes.dialogBody}>
            <KYBStepBanner activeStep={activePage} incrementStep={() => incrementPage()} />
            <Box className={classes.content}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography variant="h5" className={classes.contentTitle}>
                            Transaction Information
                        </Typography>
                        <Typography variant="subtitle1" style={{ color: '#A8B6C1' }}>
                            This will help us verify your business quickly
                        </Typography>
                    </Box>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={handleClose}
                        aria-label="close"
                        sx={{ alignSelf: 'flex-start' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box
                    sx={{
                        marginTop: { lg: '45px', md: '45px', sm: 'auto', xs: 'auto' }
                    }}
                    flexDirection="column">
                    <FormControl className={classes.formControl} style={{ rowGap: '.5em' }}>
                        <Box display="flex" flexDirection="row" alignItems="center">
                            <Typography variant="subtitle2">Transaction Type</Typography>
                            <BlueToolTip
                                placement="top"
                                arrow
                                title={
                                    <Typography className="tooltip-stat-text white-text">
                                        This is how you are expected to fund your DeFi Card Account, if you want to prefund your
                                        account via Wires select Wire In if you would like to request a daily credit line that
                                        initiates an automatically ACH Pull to recuperate outstanding balance select ACH In.
                                    </Typography>
                                }>
                                <HelpIcon sx={{ color: '#617380', fontSize: '12px', marginLeft: '5px' }} />
                            </BlueToolTip>
                        </Box>
                        <RadioGroup name="transaction-type-group" row onChange={handleRadioChange}>
                            <FormControlLabel value="ach" control={<Radio />} label="ACH in" />
                            <FormControlLabel value="wire" control={<Radio />} label="Wire in" />
                        </RadioGroup>
                    </FormControl>
                </Box>
                {transactionType && (
                    <>
                        <Box marginTop="20px" flexDirection="column">
                            <FormControl className={classes.formControl} style={{ rowGap: '.5em' }}>
                                <Box display="flex" flexDirection="row" alignItems="center">
                                    <Typography variant="subtitle2">Expected Transaction Activity</Typography>
                                    <BlueToolTip
                                        placement="top"
                                        arrow
                                        title={
                                            <Typography className="tooltip-stat-text white-text">
                                                This is the amount in activity you will have throughout the year, the information
                                                captured in this field will be used to further protect your account by assisting
                                                the bank in detecting potential ongoing fraudulent or unusual activity. This will
                                                not be used for account approval purposes or have any material impacts on your
                                                account.
                                            </Typography>
                                        }>
                                        <HelpIcon sx={{ color: '#617380', fontSize: '12px', marginLeft: '5px' }} />
                                    </BlueToolTip>
                                </Box>
                                <Autocomplete
                                    disablePortal
                                    id="transaction_volume"
                                    autoSelect={true}
                                    options={volumeOptions}
                                    getOptionLabel={option => option.label}
                                    sx={{
                                        width: {
                                            lg: 300,
                                            md: 300,
                                            sm: '100%',
                                            xs: '100%'
                                        }
                                    }}
                                    renderInput={params => <TextField {...params} placeholder="Select the range" />}
                                    onChange={handleVolumeChange}
                                    value={volumeOptions.find(option => option.value === projectionValues.transaction_volume)}
                                    size="small"
                                    variant="outlined"
                                />
                            </FormControl>
                        </Box>

                        <Box marginTop="20px" flexDirection="column">
                            <FormControl className={classes.formControl} style={{ rowGap: '.5em' }}>
                                <Box display="flex" flexDirection="row" alignItems="center">
                                    <Typography variant="subtitle2">Expected Transaction Count</Typography>
                                    <BlueToolTip
                                        placement="top"
                                        arrow
                                        title={
                                            <Typography className="tooltip-stat-text white-text">
                                                Estimated number of annual funding events. For ACH In, it represents the total
                                                days in a year when the business will engage in expense activities.
                                            </Typography>
                                        }>
                                        <HelpIcon sx={{ color: '#617380', fontSize: '12px', marginLeft: '5px' }} />
                                    </BlueToolTip>
                                </Box>
                                <Autocomplete
                                    disablePortal
                                    id="transaction_count"
                                    autoSelect={true}
                                    options={countOptions}
                                    getOptionLabel={option => option.label}
                                    sx={{
                                        width: {
                                            lg: 300,
                                            md: 300,
                                            sm: '100%',
                                            xs: '100%'
                                        }
                                    }}
                                    renderInput={params => <TextField {...params} placeholder="Select the range" />}
                                    onChange={handleCountChange}
                                    value={countOptions.find(option => option.value === projectionValues.transaction_count)}
                                    size="small"
                                    variant="outlined"
                                />
                            </FormControl>
                        </Box>
                    </>
                )}
                <Box className={classes.informationOptions}>
                    <Button variant="text" onClick={() => decreasePage()}>
                        Back
                    </Button>
                    {loading ? (
                        <CircularComponent />
                    ) : (
                        <Button variant="contained" disabled={isNextDisabled()} onClick={() => sendInfo()}>
                            Next
                        </Button>
                    )}
                </Box>
            </Box>
            <StatusSnackbar
                label={label}
                severity={'error'}
                open={openAlert}
                needsAction={true}
                decreasePage={decreasePage}
                setOpenAlert={setOpenAlert}
            />
        </Box>
    );
};
